/**
 * `<NoteIdRow>` — the `NOTE <uuid>` row that pins to the top of the editor
 * right rail (P3, GAP_ANALYSIS.md §E8 + v4 editor.css line 385).
 *
 * Useful for ops — when a user reports an issue, the support flow is "open
 * the note, copy the uuid, send it." The row is a thin mono strip with the
 * label, the truncated id, and a copy button. Clipboard write uses the
 * standard `navigator.clipboard` API; falls back silently if unavailable
 * (the row stays visible as a read-only id).
 *
 * Renders nothing when noteId is null (scratch buffer has no id yet).
 */

import { useCallback, useEffect, useState } from 'react';
import type { NoteId } from '@/types';

interface NoteIdRowProps {
  noteId: NoteId | null;
}

export function NoteIdRow({ noteId }: NoteIdRowProps): JSX.Element | null {
  const [copied, setCopied] = useState(false);

  // Reset the "copied" tick after 1.4s so the icon flips back to "copy".
  useEffect(() => {
    if (!copied) return;
    const t = window.setTimeout(() => setCopied(false), 1400);
    return () => window.clearTimeout(t);
  }, [copied]);

  const onCopy = useCallback(async () => {
    if (noteId === null) return;
    try {
      await navigator.clipboard.writeText(noteId);
      setCopied(true);
    } catch {
      // Clipboard unavailable (HTTP, sandboxed iframe, etc.) — fail silently.
      // The user can still triple-click + copy the id manually.
    }
  }, [noteId]);

  if (noteId === null) return null;

  return (
    <div className="note-id-row">
      <span className="note-id-row__label">Note</span>
      <span className="note-id-row__id" title={noteId}>{noteId}</span>
      <button
        type="button"
        className="note-id-row__copy"
        onClick={() => void onCopy()}
        data-copied={copied || undefined}
        title={copied ? 'Copied!' : 'Copy note id'}
        aria-label="Copy note id"
      >
        {copied ? <CheckGlyph /> : <CopyGlyph />}
      </button>
    </div>
  );
}

function CopyGlyph(): JSX.Element {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 13 13"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <path d="M5 3V2a1 1 0 0 1 1-1h5a1 1 0 0 1 1 1v5a1 1 0 0 1-1 1h-1" />
    </svg>
  );
}

function CheckGlyph(): JSX.Element {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 13 13"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 7l3 3 5-6" />
    </svg>
  );
}
